import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import api from '@/lib/api';
import useSchool, { BRAND_COLOR_FALLBACK } from '@/hooks/useSchool';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { ArrowLeft, ArrowRight, Send, Loader2, Mic, Square } from 'lucide-react';

function wordCount(s) {
  if (!s) return 0;
  return s.trim().split(/\s+/).filter(Boolean).length;
}

export default function StudentAssessmentAttempt() {
  const navigate = useNavigate();
  const { aid } = useParams();
  const [data, setData] = useState(null);
  const [attempt, setAttempt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [answers, setAnswers] = useState({});
  const [idx, setIdx] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [child, setChild] = useState(null);
  const school = useSchool(child?.school_id);
  const saveTimer = useRef(null);

  const minWords = data?.min_reason_words || 10;
  const maxWords = 200;
  const questions = data?.questions || [];

  // Voice recording state for reasoning input
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [transcribing, setTranscribing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingIntervalRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Start (idempotent — returns existing attempt if present)
      const startRes = await api.post(`/me/assessments/${aid}/start`);
      const att = startRes.data?.attempt;
      setAttempt(att);
      // Load assessment + safe questions
      const detailRes = await api.get(`/me/assessments/${aid}`);
      setData(detailRes.data);
      // Hydrate any saved answers
      const seed = {};
      for (const a of att?.answers || []) {
        if (a.chosen_option_id || a.student_reason) {
          seed[a.question_id] = {
            chosen_option_id: a.chosen_option_id || null,
            student_reason: a.student_reason || '',
          };
        }
      }
      setAnswers(seed);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load assessment');
      navigate('/student/assessments');
    } finally { setLoading(false); }
  }, [aid, navigate]);

  useEffect(() => {
    (async () => {
      try {
        const meRes = await api.get('/auth/me');
        if (meRes.data?.child_id) {
          const cRes = await api.get(`/children/${meRes.data.child_id}`);
          setChild(cRes.data);
        }
      } catch (err) {
        console.warn('[StudentAssessmentAttempt] child fetch failed:', err);
      }
    })();
    load();
  }, [load]);

  const brandColor = school?.brand_color || BRAND_COLOR_FALLBACK;
  const total = questions.length;
  const current = questions[idx];
  const currentAns = current ? (answers[current.question_id] || {}) : {};

  const updateAnswer = (qid, patch) => {
    setAnswers((prev) => ({
      ...prev,
      [qid]: { ...(prev[qid] || {}), ...patch },
    }));
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      audioChunksRef.current = [];
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };
      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        const reader = new FileReader();
        reader.onload = async () => {
          const base64 = reader.result.split(',')[1];
          setTranscribing(true);
          try {
            const res = await api.post('/chat/transcribe-audio', { audio_base64: base64 });
            const transcribed = (res.data?.text || '').trim();
            if (!transcribed) { toast.error('Could not understand the audio. Please try again.'); return; }
            // Append transcribed text to existing reasoning
            if (current) {
              const existing = (answers[current.question_id]?.student_reason || '').trim();
              const combined = existing ? `${existing} ${transcribed}` : transcribed;
              // Truncate to maxWords
              const words = combined.trim().split(/\s+/).filter(Boolean);
              const final = words.length > maxWords ? words.slice(0, maxWords).join(' ') : combined;
              if (words.length > maxWords) toast.info(`Trimmed to ${maxWords} words.`);
              updateAnswer(current.question_id, { student_reason: final });
            }
          } catch {
            toast.error('Transcription failed. Please try again or type your answer.');
          } finally { setTranscribing(false); }
        };
        reader.readAsDataURL(blob);
      };
      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);
      recordingIntervalRef.current = setInterval(() => setRecordingTime(t => t + 1), 1000);
    } catch {
      toast.error('Could not access microphone. Please allow microphone access.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
    clearInterval(recordingIntervalRef.current);
  };

  // Autosave (debounced) on answer change
  useEffect(() => {
    if (!attempt?.attempt_id) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      const payload = Object.entries(answers).map(([qid, v]) => ({
        question_id: qid,
        chosen_option_id: v.chosen_option_id || null,
        student_reason: v.student_reason || null,
      }));
      if (payload.length === 0) return;
      api.patch(`/me/attempts/${attempt.attempt_id}/answer`, { answers: payload }).catch(() => {});
    }, 1200);
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
  }, [answers, attempt?.attempt_id]);

  const progressFilled = useMemo(() => {
    let n = 0;
    for (const q of questions) {
      const a = answers[q.question_id];
      const wc = wordCount(a?.student_reason);
      if (a?.chosen_option_id && wc >= minWords && wc <= maxWords) n += 1;
    }
    return n;
  }, [answers, questions, minWords, maxWords]);

  const canNext = useMemo(() => {
    if (!current) return false;
    const a = answers[current.question_id];
    const wc = wordCount(a?.student_reason);
    return !!(a?.chosen_option_id && wc >= minWords && wc <= maxWords);
  }, [current, answers, minWords, maxWords]);

  const canSubmit = progressFilled === total && total > 0;

  const onSubmit = async () => {
    if (!canSubmit || !attempt?.attempt_id) {
      toast.error(`Answer all ${total} questions with a reason of at least ${minWords} words.`);
      return;
    }
    setSubmitting(true);
    try {
      const payload = questions.map((q) => {
        const a = answers[q.question_id] || {};
        return {
          question_id: q.question_id,
          chosen_option_id: a.chosen_option_id,
          student_reason: a.student_reason,
        };
      });
      const res = await api.post(`/me/attempts/${attempt.attempt_id}/submit`, { answers: payload });
      const released = res.data?.auto_released;
      toast.success(released ? 'Submitted! Results are ready.' : 'Submitted! Awaiting teacher review.');
      navigate(`/student/results/${attempt.attempt_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Submit failed');
    } finally { setSubmitting(false); }
  };

  if (loading || !current) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50 pb-32"
      data-testid="student-assessment-attempt"
    >
      <header
        className="border-b bg-white/90 backdrop-blur sticky top-0 z-10"
        style={{ borderTopWidth: 4, borderTopColor: brandColor }}
      >
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/student/assessments')} data-testid="back-btn">
            <ArrowLeft size={16} className="mr-1.5" /> Exit
          </Button>
          <div className="flex-1">
            <h1 className="font-bold text-slate-900 truncate" data-testid="attempt-assessment-title">
              {data?.assessment?.title}
            </h1>
            <p className="text-xs text-slate-500">
              Question {idx + 1} of {total} · {progressFilled}/{total} complete
            </p>
          </div>
          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-300">In progress</Badge>
        </div>
        {/* Progress bar */}
        <div className="h-1 bg-slate-100">
          <div
            className="h-1 transition-all"
            style={{ width: `${(progressFilled / total) * 100}%`, backgroundColor: brandColor }}
            data-testid="attempt-progress-bar"
          />
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8">
        <div className="bg-white border rounded-xl p-6 shadow-sm" data-testid={`attempt-q-${current.question_id}`}>
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">
            Question {idx + 1}
          </div>
          <h2 className="text-lg sm:text-xl font-semibold text-slate-900 mb-5" data-testid="attempt-prompt">
            {current.prompt}
          </h2>

          <div className="space-y-2 mb-6">
            {current.options.map((opt) => {
              const selected = currentAns.chosen_option_id === opt.id;
              return (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => updateAnswer(current.question_id, { chosen_option_id: opt.id })}
                  className={`w-full flex items-start gap-3 px-4 py-3 rounded-xl border text-left transition-colors ${selected ? 'border-indigo-500 bg-indigo-50' : 'border-slate-200 hover:border-slate-300 bg-white'}`}
                  data-testid={`option-${current.question_id}-${opt.id}`}
                >
                  <span className={`font-mono w-6 ${selected ? 'text-indigo-700 font-bold' : 'text-slate-500'}`}>{opt.id}.</span>
                  <span className={selected ? 'text-indigo-900' : 'text-slate-700'}>{opt.text}</span>
                </button>
              );
            })}
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-semibold text-slate-700">
                Why did you pick this answer? <span className="text-rose-500">*</span>
              </Label>
              {!isRecording && !transcribing && (
                <button
                  type="button"
                  onClick={startRecording}
                  className="flex items-center gap-1.5 text-xs font-medium text-indigo-600 hover:text-indigo-800 bg-indigo-50 hover:bg-indigo-100 px-2.5 py-1.5 rounded-lg transition-colors"
                  data-testid={`mic-btn-${current.question_id}`}
                >
                  <Mic size={14} /> Speak
                </button>
              )}
            </div>
            <p className="text-xs text-slate-500">
              Explain your reasoning in {minWords}–{maxWords} words. This helps your teacher give better feedback.
            </p>

            {isRecording && (
              <div className="flex items-center gap-3 p-3 rounded-xl bg-rose-50 border border-rose-200" data-testid="recording-indicator">
                <div className="w-3 h-3 rounded-full bg-rose-500 animate-pulse" />
                <span className="text-sm font-medium text-rose-700">Recording... {recordingTime}s</span>
                <button
                  type="button"
                  onClick={stopRecording}
                  className="ml-auto flex items-center gap-1.5 text-xs font-medium text-rose-700 bg-rose-100 hover:bg-rose-200 px-3 py-1.5 rounded-lg transition-colors"
                  data-testid="stop-recording-btn"
                >
                  <Square size={12} /> Stop
                </button>
              </div>
            )}

            {transcribing && (
              <div className="flex items-center gap-2 p-3 rounded-xl bg-blue-50 border border-blue-200" data-testid="transcribing-indicator">
                <Loader2 size={16} className="animate-spin text-blue-600" />
                <span className="text-sm font-medium text-blue-700">Transcribing your voice...</span>
              </div>
            )}

            <Textarea
              value={currentAns.student_reason || ''}
              onChange={(e) => {
                const words = e.target.value.trim().split(/\s+/).filter(Boolean);
                if (words.length > maxWords) return; // block typing beyond max
                updateAnswer(current.question_id, { student_reason: e.target.value });
              }}
              rows={4}
              placeholder="I picked this because…"
              disabled={isRecording || transcribing}
              data-testid={`reason-${current.question_id}`}
            />
            <div className="flex items-center justify-between text-xs">
              {(() => {
                const wc = wordCount(currentAns.student_reason);
                const isMin = wc >= minWords;
                const isOver = wc > maxWords;
                return (
                  <span className={isOver ? 'text-rose-600 font-medium' : isMin ? 'text-emerald-600' : 'text-slate-400'}>
                    {wc} / {minWords}–{maxWords} words
                    {isOver && ' (too many)'}
                  </span>
                );
              })()}
            </div>
          </div>
        </div>
      </main>

      <div className="fixed bottom-0 inset-x-0 border-t bg-white/95 backdrop-blur" data-testid="attempt-nav-bar">
        <div className="max-w-3xl mx-auto px-6 py-3 flex items-center justify-between gap-3">
          <Button
            variant="outline"
            disabled={idx === 0}
            onClick={() => setIdx((i) => Math.max(0, i - 1))}
            data-testid="prev-btn"
          >
            <ArrowLeft size={14} className="mr-1.5" /> Previous
          </Button>
          {idx < total - 1 ? (
            <Button
              onClick={() => setIdx((i) => Math.min(total - 1, i + 1))}
              disabled={!canNext}
              style={{ backgroundColor: brandColor }}
              className="text-white"
              data-testid="next-btn"
            >
              Next <ArrowRight size={14} className="ml-1.5" />
            </Button>
          ) : (
            <Button
              onClick={onSubmit}
              disabled={!canSubmit || submitting}
              style={{ backgroundColor: brandColor }}
              className="text-white"
              data-testid="submit-btn"
            >
              {submitting ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Send size={14} className="mr-1.5" />}
              Submit
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
